import type { CSSProperties } from "react";
import { useId } from "react";
import {
  useFormContext,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import { FormControlLabel, FormControl } from "@mui/material";
import MuiCheckbox from "@mui/material/Checkbox";
import { useTranslations } from "next-intl";
import { getValidationRules } from "./utils/getValidationRules";
import { HelperMessage } from "./HelperMessage";
import { CheckboxCheckedIcon, CheckboxIcon } from "./CheckboxIcon";

export interface FormCheckboxComponents {
  checkIcon: React.ReactNode;
  uncheckIcon: React.ReactNode;
}

interface FormCheckboxProps {
  className?: string;
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  rules?: RegisterOptions;
  labelStyle?: CSSProperties;
  size?: "medium" | "small";
  theme?: "dark" | "light";
  color?: string;
  showError?: boolean;
  components?: FormCheckboxComponents;
  labelAlignment?: "top" | "center";
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const FormCheckbox: React.FC<FormCheckboxProps> = ({
  className,
  name,
  label = "",
  required = true,
  rules = {},
  size = "medium",
  showError = true,
  components = {
    checkIcon: <CheckboxCheckedIcon />,
    uncheckIcon: <CheckboxIcon />,
  },
  labelAlignment = "center",
  onChange,
}) => {
  const id = useId();
  const t = useTranslations();
  const validationRules = getValidationRules({
    required,
    rules,
    t,
  });

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    field: { onChange: fieldOnChange, value },
  } = useController<Record<string, string>>({
    name,
    rules: validationRules,
    control,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    fieldOnChange(event);
    onChange?.(event);
  };

  return (
    <FormControl className={className ?? "text-sm font-medium"}>
      <FormControlLabel
        className="flex gap-2"
        control={
          <MuiCheckbox
            checked={Boolean(value)}
            checkedIcon={components.checkIcon}
            icon={components.uncheckIcon}
            id={id}
            name={name}
            onChange={handleChange}
            size={size}
            value={value}
          />
        }
        htmlFor={id}
        label={label}
        sx={{
          display: "flex",
          alignItems: labelAlignment === "top" ? "flex-start" : "center",
        }}
      />
      <HelperMessage errors={errors} name={name} showError={showError} />
    </FormControl>
  );
};
